import AgoraBigSelector from '@components/V3/Utils/InputsV3/AgoraBigSelector';
import { zodResolver } from '@hookform/resolvers/zod';
import { useUpdateSession } from '@actions';
import { GetSessionDTO } from '@shared/clientTypes/DTOs/EnrollmentsDTO';
import useToast from 'apps/agora/src/hooks/useToast';
import React, { useEffect } from 'react';
import { useController, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { z } from 'zod';
import SessionModalFormWrapper from '../../DetailsModalFormWrapper';
import { SESSION_STATUSES_OPTIONS } from '@shared/constants';
import { useAuthState } from '@contexts/AuthContext';
import { SessionStatus, UserRole } from '@shared/enums';

interface SessionDetailsFormProps {
  defaultData: GetSessionDTO;
  sessionId: string;
  onCloseEditMode: () => void;
}

const formDataSchema = z.object({
  status: z.number(),
});

type FormData = z.infer<typeof formDataSchema>;

const SessionDetailsForm = (props: SessionDetailsFormProps) => {
  const { roles } = useAuthState();
  const canSettleSessions =
    roles.includes(UserRole.Financial) || roles.includes(UserRole.SuperUser);
  const settledStatuses: number[] = [SessionStatus.PAID, SessionStatus.UNPAYABLE];
  const statusOptions = canSettleSessions
    ? SESSION_STATUSES_OPTIONS
    : SESSION_STATUSES_OPTIONS.filter((option) => !settledStatuses.includes(option.value));
  const { defaultData, sessionId, onCloseEditMode } = props;

  const toast = useToast();
  const query = useQueryClient();

  const { handleSubmit, trigger, setValue, control, getValues } = useForm<FormData>({
    resolver: zodResolver(formDataSchema),
    reValidateMode: 'onBlur',
    mode: 'onBlur',
    defaultValues: {
      status: defaultData?.status,
    },
  });

  const { field: statusField, fieldState: statusFieldState } = useController({
    name: 'status',
    control,
  });

  const { mutate: updateSession, isLoading } = useUpdateSession(sessionId, {
    onSuccess: async () => {
      toast.success('Successfully updated the enrollment.');
      query.invalidateQueries('/enrollments/:id');
      onCloseEditMode();
    },
    onError: () => {
      toast.error('Enrollment could not be updated.');
    },
  });

  const onSubmit = async (data: FormData) => {
    const isValid = await trigger();
    if (isValid) {
      updateSession({
        status: data.status,
      });
    }
  };

  const touchAllFields = (fields: Record<keyof FormData, string | number>) => {
    Object.keys(fields).forEach((key) => {
      setValue(key as keyof FormData, getValues()[key as keyof FormData], {
        shouldTouch: true,
        shouldValidate: true,
      });
    });
  };

  const submitHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
    const formData = getValues();

    touchAllFields(formData);

    handleSubmit(onSubmit)(e);
  };

  useEffect(() => {
    const formData = getValues();

    touchAllFields(formData);
  }, []);

  return (
    <SessionModalFormWrapper
      title={'Details'}
      isLoading={isLoading}
      onCancel={onCloseEditMode}
      submitHandler={submitHandler}
    >
      <AgoraBigSelector
        value={statusField.value}
        onSelect={(value) => statusField.onChange(value)}
        options={statusOptions}
        onBlur={statusField.onBlur}
        labelKey="label"
        valueKey="value"
        label="Status"
        isValid={!statusFieldState.error}
        isTouched={statusFieldState.isTouched}
        errorText={statusFieldState.error?.message}
        isRequired
      />
    </SessionModalFormWrapper>
  );
};

export default SessionDetailsForm;
